import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, X, Loader2 } from "lucide-react";
import api from "../../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const errMsg = (e) => { const d = e.response?.data?.detail; return d?.message || (typeof d === "string" ? d : "Something went wrong"); };

const Badge = ({ children, tone = "zinc" }) => {
  const c = { zinc: "border-zinc-300 text-zinc-600", blue: "border-[#0033FF] text-[#0033FF]", red: "border-[#FF3B30] text-[#FF3B30]", green: "border-emerald-600 text-emerald-700" }[tone];
  return <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 border ${c}`}>{children}</span>;
};

function AccountDrawer({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const load = () => api.get(`/owner/accounts/${id}`).then(({ data }) => setD(data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const act = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); toast.success(msg); setNote(""); await load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  const a = d?.account;
  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/50 flex justify-end" onClick={onClose} data-testid="owner-account-drawer">
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF]">Account</div>
          <button onClick={onClose} data-testid="owner-drawer-close"><X className="w-4 h-4" /></button>
        </div>
        {!a ? <Loader2 className="w-5 h-5 animate-spin text-[#0033FF]" /> : (
          <>
            <h2 className="text-2xl font-black tracking-tighter">{a.name}</h2>
            <div className="text-sm text-zinc-500 mb-3">{a.email}</div>
            <div className="flex flex-wrap gap-2 mb-6">
              <Badge tone="blue">{a.plan}</Badge>
              {a.comped && <Badge tone="green">Free plan</Badge>}
              {a.paused && <Badge tone="red">Paused</Badge>}
              {a.stripe_subscription_id && <Badge>Paying</Badge>}
              {d.owner && <Badge>Owner</Badge>}
            </div>
            <dl className="grid grid-cols-2 gap-y-2 text-sm mb-6">
              <dt className="text-zinc-500">Signed up</dt><dd>{fmt(a.created_at)}</dd>
              <dt className="text-zinc-500">Last sign-in</dt><dd>{fmt(a.last_login_at)}</dd>
              <dt className="text-zinc-500">Musings / content</dt><dd>{d.stats.musings} / {d.stats.content}</dd>
              <dt className="text-zinc-500">Profiles</dt><dd>{d.onboarding.company_done ? "Company ✓" : "Company —"} · {d.onboarding.personal_done ? "Personal ✓" : "Personal —"}</dd>
              <dt className="text-zinc-500">Team role</dt><dd>{a.org_role || "Solo"}</dd>
              {a.comp_note && <><dt className="text-zinc-500">Free plan note</dt><dd>{a.comp_note}</dd></>}
              {a.pause_reason && <><dt className="text-zinc-500">Pause reason</dt><dd>{a.pause_reason}</dd></>}
            </dl>
            {!d.owner && (
              <div className="border border-zinc-200 p-4 mb-6 space-y-3" data-testid="owner-actions">
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Owner actions</div>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note or reason (optional)" data-testid="owner-action-note"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#0033FF]" />
                <div className="flex flex-wrap gap-2">
                  {["executive", "enterprise"].map((p) => (
                    <button key={p} disabled={!!busy || (a.comped && a.plan === p)} data-testid={`owner-comp-${p}`}
                      onClick={() => act(p, () => api.post(`/owner/accounts/${id}/comp`, { plan: p, note }), `Gave free ${p}`)}
                      className="bg-[#0033FF] text-white px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-40">
                      {busy === p ? "…" : `Give free ${p}`}
                    </button>
                  ))}
                  {a.comped && (
                    <button disabled={!!busy} data-testid="owner-comp-remove"
                      onClick={() => act("none", () => api.post(`/owner/accounts/${id}/comp`, { plan: "none" }), "Free plan removed")}
                      className="border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white disabled:opacity-40">Remove free plan</button>
                  )}
                  <button disabled={!!busy} data-testid="owner-pause-toggle"
                    onClick={() => (a.paused || window.confirm(`Pause ${a.email}? They won't be able to sign in or get emails.${a.stripe_subscription_id ? "\n\nThey have a paid subscription, and Stripe will keep billing them. Cancel or pause it in Stripe if you don't want that." : ""}`)) &&
                      act("pause", () => api.post(`/owner/accounts/${id}/pause`, { paused: !a.paused, reason: note }), a.paused ? "Account unpaused" : "Account paused")}
                    className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border disabled:opacity-40 ${a.paused ? "border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white" : "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"}`}>
                    {a.paused ? "Unpause account" : "Pause account"}
                  </button>
                </div>
              </div>
            )}
            <Section title="Payments">{d.payments.length ? d.payments.map((p) => (
              <div key={p.session_id} className="flex justify-between text-sm py-1.5"><span>{fmt(p.created_at)} · {p.plan} ({p.billing_cycle})</span><span className="font-mono">${p.amount} · {p.payment_status}</span></div>
            )) : <Empty />}</Section>
            <Section title="Support requests">{d.support.length ? d.support.map((t) => (
              <div key={t.id} className="text-sm py-1.5"><span className="font-mono text-xs">{t.ref}</span> · {t.subject} <Badge tone={t.status === "open" ? "red" : "zinc"}>{t.status}</Badge></div>
            )) : <Empty />}</Section>
            <Section title="Cancellations">{d.cancellations.length ? d.cancellations.map((c) => (
              <div key={c.id} className="text-sm py-1.5">{fmt(c.created_at)} · {c.reason || "No reason"}{c.comment && ` — "${c.comment}"`}</div>
            )) : <Empty />}</Section>
          </>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-5"><div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600 mb-1 border-b border-zinc-200 pb-1">{title}</div>{children}</div>
);
const Empty = () => <div className="text-xs text-zinc-400 py-1.5">None</div>;

export const OwnerAccounts = () => {
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/owner/accounts", { params: { q, plan, status } }).then(({ data }) => setRows(data.accounts)).catch(() => setRows([]));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, plan, status]); // eslint-disable-line

  const sel = "px-3 py-2.5 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#0033FF]";
  return (
    <div data-testid="owner-accounts">
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" data-testid="owner-accounts-search" className={`${sel} w-full pl-9`} />
        </div>
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className={sel} data-testid="owner-accounts-plan-filter">
          <option value="">All plans</option><option value="entry">Entry</option><option value="executive">Executive</option><option value="enterprise">Enterprise</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={sel} data-testid="owner-accounts-status-filter">
          <option value="">Any status</option><option value="paying">Paying</option><option value="comped">Free plan</option><option value="paused">Paused</option>
        </select>
      </div>
      <div className="border border-zinc-200 overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead><tr className="text-left text-[10px] tracking-[0.2em] uppercase text-zinc-500 border-b border-zinc-200">
            <th className="p-3">Leader</th><th className="p-3">Plan</th><th className="p-3">Team</th><th className="p-3">Signed up</th><th className="p-3">Last sign-in</th><th className="p-3 text-right">Musings</th>
          </tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={6} className="p-6"><Loader2 className="w-5 h-5 animate-spin text-[#0033FF]" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={6} className="p-6 text-zinc-500" data-testid="owner-accounts-empty">No accounts match.</td></tr>}
            {rows?.map((r) => (
              <tr key={r.id} onClick={() => setOpen(r.id)} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`owner-account-row-${r.id}`}>
                <td className="p-3"><div className="font-bold">{r.name}</div><div className="text-xs text-zinc-500">{r.email}</div></td>
                <td className="p-3"><div className="flex flex-wrap gap-1"><Badge tone="blue">{r.plan}</Badge>{r.paying && <Badge>Paying</Badge>}{r.comped && <Badge tone="green">Free</Badge>}{r.paused && <Badge tone="red">Paused</Badge>}</div></td>
                <td className="p-3 text-xs text-zinc-600">{r.org_name ? `${r.org_name} · ${r.org_role}` : "Solo"}</td>
                <td className="p-3 text-xs">{fmt(r.created_at)}</td>
                <td className="p-3 text-xs">{fmt(r.last_login_at)}</td>
                <td className="p-3 text-right font-mono">{r.musings}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows && <div className="text-xs text-zinc-500 mt-2">{rows.length} account{rows.length === 1 ? "" : "s"}</div>}
      {open && <AccountDrawer id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};
